import React, { useState } from 'react';
import './Books.css';

const services = [
  {
    id: '01',
    anchor: 'audit-assurance',
    title: 'Audit & Assurance',
    tagline: 'Independent. Thorough. Trusted.',
    description:
      'Comprehensive audit services conducted with rigour and independence — from statutory audits to stock verification and investment certification.',
    tags: ['STATUTORY', 'TAX AUDIT', 'GST AUDIT', 'INTERNAL'],
    accent: '#c0392b',
    points: [
      'Statutory Audit under the Companies Act',
      'Tax Audit under Income Tax Act',
      'GST Audit',
      'Internal Audit — Statutory & Management',
      'Stock Audit — physical verification & inspection',
      'Certification of Investments & Debtors',
    ],
  },
  {
    id: '02',
    anchor: 'tax-compliance',
    title: 'Tax & Compliance',
    tagline: 'Compliant today. Prepared for tomorrow.',
    description:
      'End-to-end statutory compliance across Companies Act, Income Tax Act, GST, and allied business laws — keeping you on the right side of every regulation.',
    tags: ['INCOME TAX', 'GST', 'TDS', 'ROC'],
    accent: '#1a4a9e',
    points: [
      'Statutory compliance — Companies Act, IT Act & GST',
      'Tax planning for firms, companies & individuals',
      'TDS returns filing & reconciliation',
      'ROC related work & filings',
    ],
  },
  {
    id: '03',
    anchor: 'accounting-mis',
    title: 'Accounting & MIS',
    tagline: 'Numbers that tell your story.',
    description:
      'Dedicated accounting teams manage your books end-to-end, paired with sharp MIS reporting that gives management real-time visibility into performance.',
    tags: ['MIS', 'ACCOUNTING', 'BUDGETING', 'TALLY ERP'],
    accent: '#4a9eff',
    points: [
      'MIS reporting — Sales, Expenses & Budgeting',
      'Debtor analysis & working capital review',
      'Full accounts department outsourcing',
      'Internal controls via Tally ERP Software',
    ],
  },
  {
    id: '04',
    anchor: 'ai-solutions',
    title: 'AI Solutions',
    tagline: 'Smarter systems. Faster decisions.',
    description:
      'AI-powered support for accounting, office management, taxation, and routine workflows to improve speed, accuracy, and decision-making.',
    tags: ['AI ACCOUNTING', 'OFFICE OPS', 'TAXATION AI', 'AUTOMATION'],
    accent: '#2a6bbf',
    points: [
      'AI-assisted accounting process optimization',
      'Office management workflow automation',
      'AI support for taxation analysis and compliance checks',
      'Data-driven operational insights and reporting',
    ],
  },
  {
    id: '05',
    anchor: 'labour-corporate-law',
    title: 'Labour & Corporate Law',
    tagline: 'Protect your people. Protect your business.',
    description:
      'Advisory and compliance services across ESIC, Provident Fund, Labour Law, and Company Law — ensuring your workforce and entity stay fully compliant.',
    tags: ['ESIC', 'PF', 'LABOUR LAW', 'COMPANY LAW'],
    accent: '#1a4a9e',
    points: [
      'ESIC & PF registration and returns',
      'Labour law compliance advisory',
      'Company Law / ROC related services',
      'Regulatory filings and documentation',
    ],
  },
  {
    id: '06',
    anchor: 'business-advisory',
    title: 'Business Advisory',
    tagline: 'Strategy. Structure. Scale.',
    description:
      'Integrated advisory across tax, finance, and regulatory domains — from capital structuring and financial planning to direct and indirect tax strategy.',
    tags: ['TAX ADVISORY', 'FINANCING', 'PLANNING', 'CONSULTING'],
    accent: '#4a9eff',
    points: [
      'Direct & indirect tax advisory',
      'Financing & capital structuring support',
      'Strategic financial planning',
      'Business advisory & regulatory consulting',
    ],
  },
];

const Services = () => {
  const [activeId, setActiveId] = useState(null);

  return (
    <div className="svc-page">

      {/* ── HERO ─────────────────────────────────────── */}
      <section className="svc-hero">
        <div className="svc-hero-inner">
          <p className="svc-hero-eyebrow">What we do</p>
          <h1 className="svc-hero-headline">
            Comprehensive services<br />
            <em>under one roof</em>
          </h1>
          <p className="svc-hero-sub">
            RSC offers a wide spectrum of integrated professional services — enabling clients to access multidisciplinary expertise through a single, cohesive platform.
          </p>
        </div>
        <div className="svc-badge-strip">
          {services.map(svc => (
            <span
              key={svc.id}
              className="svc-badge"
              onClick={() => {
                const el = document.getElementById(svc.anchor);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              style={{ cursor: 'pointer' }}
            >
              {svc.title}
            </span>
          ))}
        </div>
      </section>

      {/* ── SERVICE CARDS GRID ───────────────────────── */}
      <section className="svc-grid-section">
        <div className="svc-grid">
          {services.map((svc) => (
            <article
              key={svc.id}
              id={svc.anchor}
              className={`svc-card ${activeId === svc.id ? 'svc-card--active' : ''}`}
              style={{ '--svc-accent': svc.accent }}
              onMouseEnter={() => setActiveId(svc.id)}
              onMouseLeave={() => setActiveId(null)}
            >
              {/* top row */}
              <div className="svc-card-top">
                <span className="svc-card-num">{svc.id}</span>
              </div>

              {/* title & tagline — always visible */}
              <h2 className="svc-card-title">{svc.title}</h2>
              <p className="svc-card-tagline">{svc.tagline}</p>

              {/* description — always visible */}
              <p className="svc-card-desc">{svc.description}</p>

              {/* bullet points — always visible */}
              <ul className="svc-card-points">
                {svc.points.map((pt, index) => (
                  <li key={pt}>
                    <span
                      className={`svc-card-bullet ${index % 2 === 0 ? 'svc-card-bullet--red' : 'svc-card-bullet--blue'}`}
                    />
                    {pt}
                  </li>
                ))}
              </ul>

              {/* tags */}
              <div className="svc-card-tags">
                {svc.tags.map(t => (
                  <span key={t} className="svc-card-tag">{t}</span>
                ))}
              </div>

              {/* bottom accent bar */}
              <div className="svc-card-bar" style={{ background: svc.accent }} />
            </article>
          ))}
        </div>
      </section>

      {/* ── WHY US STRIP ─────────────────────────────── */}
      <section className="svc-why">
        <div className="svc-why-inner">
          <h2 className="svc-why-title">Why Ritesh Shah &amp; Co.?</h2>
          <div className="svc-why-grid">
            {[
              { title: 'Precision-first', body: 'Every filing cross-checked. Zero tolerance for errors that cost you money or compliance.' },
              { title: 'Confidential', body: 'Your financial data is handled with strict professional secrecy and digital security.' },
              { title: 'Fast turnaround', body: 'Most routine filings completed within 24–48 hours of receiving your documents.' },
              { title: 'Relationship-driven', body: 'One dedicated CA who knows your business, not a call-centre ticket system.' },
            ].map(w => (
              <div key={w.title} className="svc-why-card">
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────── */}
      <section className="footer-section">
        <div className="container">
          <p>© 2026 Ritesh Shah &amp; Co. All rights reserved.</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#aaa' }}>
            Powered by{' '}
            <a href="https://elyspr.com/" target="_blank" rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none', fontWeight: 'bold' }}>
              Elyspr
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Services;
